import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

import { TREATMENT_PLAN_BASE_PATH } from '../../../../../constants';
import { history } from '../../../../../helpers';
import { treatmentPlanActions, treatmentPlanV3Actions } from '../../../../../store';
import { getTpRouteSegment, toClientOption } from '../utils';
import usePlanCreation from './usePlanCreation';
import usePlanDialogRequests from './usePlanDialogRequests';
import usePlanMutations from './usePlanMutations';
import useTreatmentPlanV3HeaderState from './useTreatmentPlanV3HeaderState';

import type { AppDispatch } from '../../../../../store';
import type {
  HeaderDialogController,
  PlanActionsDependencies,
  UsePlanActionsResult,
} from './types';

export default function useTreatmentPlanV3PlanActions(
  dialog: HeaderDialogController,
): UsePlanActionsResult {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const location = useLocation();
  const { treatmentPlan, activeTreatmentPlan, selectedClient } = useTreatmentPlanV3HeaderState();

  return useTreatmentPlanV3PlanActionsWithDependencies({
    dispatch,
    planActions: treatmentPlanActions,
    createActions: treatmentPlanV3Actions,
    getAccessTokenSilently,
    treatmentPlan,
    activeTreatmentPlan,
    selectedClient,
    routeSegment: getTpRouteSegment(location.pathname),
    dialog,
  });
}

export function useTreatmentPlanV3PlanActionsWithDependencies({
  dispatch,
  planActions,
  createActions,
  getAccessTokenSilently,
  treatmentPlan,
  activeTreatmentPlan,
  selectedClient,
  routeSegment,
  dialog,
}: PlanActionsDependencies): UsePlanActionsResult {
  const { activeDialog, openDialog, closeDialog } = dialog;
  const treatmentPlanId = treatmentPlan?.id;
  const activeTreatmentPlanId = activeTreatmentPlan?.id;

  const requests = usePlanDialogRequests({ activeTreatmentPlanId, openDialog });
  const mutations = usePlanMutations({
    dispatch,
    planActions,
    getAccessTokenSilently,
    treatmentPlanId,
    dialog,
  });
  const creation = usePlanCreation({
    dispatch,
    createActions,
    getAccessTokenSilently,
    clientId: selectedClient?.id,
    treatmentPlanId,
    dialog,
  });

  const navigateToPlan = useCallback(
    (planId: number) => history.navigate(`${TREATMENT_PLAN_BASE_PATH}/${planId}/${routeSegment}`),
    [routeSegment],
  );

  const openActivation = useCallback(() => {
    history.navigate('/tp-activation', {
      state: { client: selectedClient ? toClientOption(selectedClient) : null },
    });
  }, [selectedClient]);

  const confirmSwitchPlan = useCallback(() => {
    if (activeDialog?.kind !== 'switchPlan') return;
    closeDialog();
    navigateToPlan(activeDialog.treatmentPlanId);
  }, [activeDialog, closeDialog, navigateToPlan]);

  const switchToActivePlan = useCallback(() => {
    closeDialog();
    if (activeTreatmentPlanId !== undefined) navigateToPlan(activeTreatmentPlanId);
  }, [activeTreatmentPlanId, closeDialog, navigateToPlan]);

  return {
    ...requests,
    confirmActivate: mutations.confirmActivate,
    confirmDeactivate: mutations.confirmDeactivate,
    confirmEdit: mutations.confirmEdit,
    confirmDelete: mutations.confirmDelete,
    confirmDownload: mutations.confirmDownload,
    createBlankPlan: creation.createBlankPlan,
    confirmClone: creation.confirmClone,
    openActivation,
    confirmSwitchPlan,
    switchToActivePlan,
    isSubmitting: mutations.isSubmitting || creation.isSubmitting,
  };
}
